// Admin Comptabilité tab — invoice CRUD + upload + OCR Gemini + CSV export + Qonto sync + Revenus.
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import QontoSyncPanel from "./QontoSyncPanel";
import RevenusPanel from "./RevenusPanel";

const STATUS_COLOR = {
  to_pay: { bg: "rgba(201,138,58,0.16)", fg: "#c98a3a", label: { fr: "À payer", en: "To pay" } },
  paid: { bg: "rgba(83,128,72,0.16)", fg: "#4f6b3a", label: { fr: "Payée", en: "Paid" } },
  disputed: { bg: "rgba(154,29,43,0.16)", fg: "#991b2b", label: { fr: "Contestée", en: "Disputed" } },
  archived: { bg: "var(--surface-2)", fg: "var(--muted)", label: { fr: "Archivée", en: "Archived" } },
};
const CATEGORIES = ["general", "restauration", "hebergement", "communication", "technique", "autre"];

const empty = () => ({
  supplier_name: "", supplier_siret: "", supplier_iban: "",
  invoice_number: "", invoice_date: "", due_date: "",
  amount_ttc: 0, amount_ht: 0, vat_amount: 0, currency: "EUR",
  category: "general", project: "fetes_2027",
  status: "to_pay", payment_reference: "", file_url: "", notes: "",
});

export default function ComptabiliteTab({ locale = "fr" }) {
  const isFr = locale === "fr";
  const [data, setData] = useState({ items: [], counts: {}, total_to_pay: 0, total_paid: 0, overdue: 0, statuses: [], status_labels: {} });
  const [editing, setEditing] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");

  const load = useCallback(async () => {
    const q = filterStatus ? `?status=${filterStatus}` : "";
    const r = await api.get(`/admin/accounting/invoices${q}`);
    setData(r.data);
  }, [filterStatus]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.supplier_name.trim()) return;
    const payload = {
      ...editing,
      amount_ttc: Number(editing.amount_ttc) || 0,
      amount_ht: Number(editing.amount_ht) || 0,
      vat_amount: Number(editing.vat_amount) || 0,
    };
    if (editing.id) await api.patch(`/admin/accounting/invoices/${editing.id}`, payload);
    else await api.post("/admin/accounting/invoices", payload);
    setEditing(null);
    load();
  };

  const remove = async (iid) => {
    if (!window.confirm(isFr ? "Supprimer cette facture ?" : "Delete this invoice?")) return;
    await api.delete(`/admin/accounting/invoices/${iid}`);
    load();
  };

  const markPaid = async (iid) => {
    await api.patch(`/admin/accounting/invoices/${iid}`, { status: "paid" });
    load();
  };

  return (
    <div className="space-y-6" data-testid="comptabilite-tab">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-2xl">{isFr ? "Comptabilité" : "Accounting"}</h2>
        <a className="ml-auto btn btn-outline btn-sm"
           data-testid="invoices-export-csv"
           href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/accounting/export.csv`}
           target="_blank" rel="noreferrer">
          📊 Export CSV
        </a>
        <button className="btn btn-primary" onClick={() => setEditing(empty())} data-testid="invoice-add-btn">
          + {isFr ? "Nouvelle facture" : "New invoice"}
        </button>
      </div>

      <div className="grid sm:grid-cols-4 gap-3">
        <Kpi label={isFr ? "À payer" : "To pay"} value={`${data.total_to_pay} €`} color="var(--gold)" testId="acct-kpi-topay" />
        <Kpi label={isFr ? "Payées" : "Paid"} value={`${data.total_paid} €`} color="var(--moss)" testId="acct-kpi-paid" />
        <Kpi label={isFr ? "En retard" : "Overdue"} value={data.overdue} color={data.overdue > 0 ? "var(--cinnabar)" : "var(--muted)"} testId="acct-kpi-overdue" />
        <Kpi label="Total" value={Object.values(data.counts).reduce((a, b) => a + b, 0)} color="var(--ink-2)" testId="acct-kpi-total" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>
          {isFr ? "Statut :" : "Status:"}
        </span>
        <button onClick={() => setFilterStatus("")} className="px-3 py-1 text-xs font-caps"
                style={{ letterSpacing: "0.15em",
                          backgroundColor: !filterStatus ? "var(--cinnabar)" : "var(--surface-2)",
                          color: !filterStatus ? "white" : "var(--ink)" }}
                data-testid="acct-filter-all">
          {isFr ? "Tous" : "All"}
        </button>
        {data.statuses.map((s) => (
          <button key={s} onClick={() => setFilterStatus(filterStatus === s ? "" : s)}
                  className="px-3 py-1 text-xs font-caps"
                  data-testid={`acct-filter-${s}`}
                  style={{ letterSpacing: "0.15em",
                            backgroundColor: filterStatus === s ? "var(--cinnabar)" : "var(--surface-2)",
                            color: filterStatus === s ? "white" : "var(--ink)" }}>
            {STATUS_COLOR[s]?.label[isFr ? "fr" : "en"] || s} ({data.counts[s] || 0})
          </button>
        ))}
      </div>

      <div className="admin-card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border)" }}>
              {[
                isFr ? "Échéance" : "Due",
                isFr ? "Fournisseur" : "Supplier",
                "N°",
                "TTC",
                isFr ? "Statut" : "Status",
                "",
              ].map((h, i) => (
                <th key={i} className="text-left p-2 font-caps text-[0.6rem]"
                    style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.items.map((it) => {
              const sc = STATUS_COLOR[it.status] || STATUS_COLOR.to_pay;
              const overdue = it.status === "to_pay" && it.due_date && it.due_date < new Date().toISOString().slice(0, 10);
              return (
                <tr key={it.id} style={{ borderBottom: "1px solid var(--border)" }}
                    data-testid={`invoice-row-${it.id}`}>
                  <td className="p-2 text-xs" style={{ color: overdue ? "var(--cinnabar)" : "var(--ink-2)" }}>
                    {overdue && "⚠ "}{it.due_date || "—"}
                  </td>
                  <td className="p-2">
                    <div className="font-medium">{it.supplier_name}</div>
                    <div className="text-[0.7rem]" style={{ color: "var(--muted)" }}>{it.category}</div>
                  </td>
                  <td className="p-2 text-xs">{it.invoice_number || "—"}</td>
                  <td className="p-2 text-sm font-mono">{(it.amount_ttc || 0).toFixed(2)} €</td>
                  <td className="p-2">
                    <span className="px-2 py-0.5 text-[0.65rem] font-caps"
                          style={{ backgroundColor: sc.bg, color: sc.fg, letterSpacing: "0.15em" }}>
                      {sc.label[isFr ? "fr" : "en"]}
                    </span>
                  </td>
                  <td className="p-2 text-right">
                    {it.file_url && (
                      <a className="btn btn-outline btn-sm mr-1"
                         href={`${process.env.REACT_APP_BACKEND_URL}${it.file_url}`}
                         target="_blank" rel="noreferrer"
                         data-testid={`invoice-view-${it.id}`}>📄</a>
                    )}
                    {it.status === "to_pay" && (
                      <button className="btn btn-outline btn-sm mr-1" onClick={() => markPaid(it.id)}
                              data-testid={`invoice-markpaid-${it.id}`}>✓ Payée</button>
                    )}
                    <button className="btn btn-outline btn-sm mr-1" onClick={() => setEditing(it)}
                            data-testid={`invoice-edit-${it.id}`}>✎</button>
                    <button className="btn btn-outline btn-sm" onClick={() => remove(it.id)}
                            data-testid={`invoice-delete-${it.id}`}>✕</button>
                  </td>
                </tr>
              );
            })}
            {data.items.length === 0 && (
              <tr><td colSpan="6" className="p-6 text-center" style={{ color: "var(--muted)" }}>
                {isFr ? "Aucune facture" : "No invoices"}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <InvoiceEditor invoice={editing} setInvoice={setEditing}
                        onSave={save} onClose={() => setEditing(null)} isFr={isFr} />
      )}

      <RevenusPanel locale={locale} />

      <QontoSyncPanel locale={locale} onReconciled={load} />
    </div>
  );
}

function Kpi({ label, value, color, testId }) {
  return (
    <div className="admin-card text-center" data-testid={testId}>
      <div className="font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>{label}</div>
      <div className="font-display text-2xl" style={{ color }}>{value}</div>
    </div>
  );
}

function InvoiceEditor({ invoice, setInvoice, onSave, onClose, isFr }) {
  const [ocrBusy, setOcrBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const update = (k, v) => setInvoice({ ...invoice, [k]: v });

  const uploadFile = async (file) => {
    setUploadBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/admin/accounting/uploads/invoice", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      update("file_url", r.data.file_url);
    } finally { setUploadBusy(false); }
  };

  const ocrFile = async (file) => {
    setOcrBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/admin/accounting/ocr-invoice", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const ext = r.data.extracted || {};
      setInvoice((cur) => ({
        ...cur,
        supplier_name: ext.supplier_name || cur.supplier_name,
        supplier_siret: ext.supplier_siret || cur.supplier_siret,
        supplier_iban: ext.supplier_iban || cur.supplier_iban,
        invoice_number: ext.invoice_number || cur.invoice_number,
        invoice_date: ext.invoice_date || cur.invoice_date,
        due_date: ext.due_date || cur.due_date,
        amount_ht: ext.amount_ht || cur.amount_ht,
        amount_ttc: ext.amount_ttc || cur.amount_ttc,
        vat_amount: ext.vat_amount || cur.vat_amount,
        currency: ext.currency || cur.currency,
      }));
    } catch (e) {
      alert((isFr ? "OCR échec : " : "OCR failed: ") + (e.response?.data?.detail || e.message));
    } finally { setOcrBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ backgroundColor: "rgba(20,16,12,0.7)" }}>
      <div className="admin-card max-w-2xl w-full max-h-[90vh] overflow-y-auto space-y-3"
           data-testid="invoice-editor">
        <h3 className="font-display text-xl">
          {invoice.id ? (isFr ? "Modifier la facture" : "Edit invoice") : (isFr ? "Nouvelle facture" : "New invoice")}
        </h3>

        {/* OCR / Upload zone */}
        <div className="p-3 flex flex-wrap gap-2 items-center"
             style={{ backgroundColor: "var(--surface-2)", border: "1px dashed var(--gold)" }}>
          <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em", color: "var(--gold)" }}>
            ◇ {isFr ? "AUTO-REMPLISSAGE OCR" : "OCR AUTOFILL"} ◇
          </span>
          <label className="btn btn-primary btn-sm cursor-pointer">
            {ocrBusy ? "⏳…" : (isFr ? "🔍 Analyser un PDF" : "🔍 Scan PDF")}
            <input type="file" accept=".pdf,image/*" hidden
                   data-testid="invoice-ocr-input"
                   onChange={(e) => e.target.files?.[0] && ocrFile(e.target.files[0])} />
          </label>
          <label className="btn btn-outline btn-sm cursor-pointer">
            {uploadBusy ? "⏳…" : (isFr ? "📎 Joindre PDF" : "📎 Attach PDF")}
            <input type="file" accept=".pdf,image/*" hidden
                   data-testid="invoice-upload-input"
                   onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])} />
          </label>
          {invoice.file_url && (
            <a className="text-xs underline" target="_blank" rel="noreferrer"
               href={`${process.env.REACT_APP_BACKEND_URL}${invoice.file_url}`}>
              ✓ {isFr ? "fichier joint" : "file attached"}
            </a>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <LabeledInput label={isFr ? "Fournisseur" : "Supplier"} value={invoice.supplier_name}
                         onChange={(v) => update("supplier_name", v)} testId="invoice-supplier" />
          <LabeledInput label="SIRET" value={invoice.supplier_siret}
                         onChange={(v) => update("supplier_siret", v)} testId="invoice-siret" />
          <LabeledInput label="IBAN" value={invoice.supplier_iban}
                         onChange={(v) => update("supplier_iban", v)} testId="invoice-iban" />
          <LabeledInput label="N° " value={invoice.invoice_number}
                         onChange={(v) => update("invoice_number", v)} testId="invoice-number" />
          <LabeledInput label={isFr ? "Date facture" : "Invoice date"} type="date" value={invoice.invoice_date}
                         onChange={(v) => update("invoice_date", v)} testId="invoice-date" />
          <LabeledInput label={isFr ? "Échéance" : "Due date"} type="date" value={invoice.due_date}
                         onChange={(v) => update("due_date", v)} testId="invoice-due" />
          <LabeledInput label="HT €" type="number" step="0.01" value={invoice.amount_ht}
                         onChange={(v) => update("amount_ht", v)} testId="invoice-ht" />
          <LabeledInput label="TVA €" type="number" step="0.01" value={invoice.vat_amount}
                         onChange={(v) => update("vat_amount", v)} testId="invoice-vat" />
          <LabeledInput label="TTC €" type="number" step="0.01" value={invoice.amount_ttc}
                         onChange={(v) => update("amount_ttc", v)} testId="invoice-ttc" />
          <label className="block">
            <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>
              {isFr ? "Catégorie" : "Category"}
            </span>
            <select className="input-classic w-full" value={invoice.category}
                    onChange={(e) => update("category", e.target.value)} data-testid="invoice-category">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>
              {isFr ? "Statut" : "Status"}
            </span>
            <select className="input-classic w-full" value={invoice.status}
                    onChange={(e) => update("status", e.target.value)} data-testid="invoice-status">
              {Object.entries(STATUS_COLOR).map(([k, v]) => (
                <option key={k} value={k}>{v.label[isFr ? "fr" : "en"]}</option>
              ))}
            </select>
          </label>
          <LabeledInput label={isFr ? "Réf. paiement" : "Payment ref"} value={invoice.payment_reference}
                         onChange={(v) => update("payment_reference", v)} testId="invoice-paymentref" />
        </div>

        <label className="block">
          <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>Notes</span>
          <textarea rows={2} className="input-classic w-full" value={invoice.notes}
                    onChange={(e) => update("notes", e.target.value)} data-testid="invoice-notes" />
        </label>

        <div className="flex gap-2 pt-2">
          <button className="btn btn-primary" onClick={onSave} data-testid="invoice-save">
            {isFr ? "Enregistrer" : "Save"}
          </button>
          <button className="btn btn-outline btn-sm" onClick={onClose} data-testid="invoice-cancel">
            {isFr ? "Annuler" : "Cancel"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LabeledInput({ label, value, onChange, type = "text", step, testId }) {
  return (
    <label className="block">
      <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{label}</span>
      <input type={type} step={step} value={value || ""}
             onChange={(e) => onChange(e.target.value)}
             className="input-classic w-full" data-testid={testId} />
    </label>
  );
}
